import { API_TYPES } from "@clipboard-health/contract-backend-main";
import { isDefined, toErrorMessage } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { SHIFT_DISCOVERY_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { MY_SHIFTS_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { useCancellationProtectionConfig } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancellationProtection/hooks/useCancellationProtectionConfig";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useLatestCancellationProtectionPayout } from "./api/useLatestCancellationProtectionPayout";
import { useInvalidateCancellationProtectionPayouts } from "./api/usePaginatedCancellationProtectionPayouts";
import { useUpdateCancellationProtectionPayout } from "./api/useUpdateCancellationProtectionPayout";
import { CancellationProtectionPayoutBottomSheet } from "./components/CancellationProtectionPayoutBottomSheet";

const TARGET_PATHS = [SHIFT_DISCOVERY_PATH, MY_SHIFTS_PATH];

export function CancellationProtectionPayoutEntrypoint() {
  const modalState = useModalState();
  const { userId: workerId } = useDefinedWorker();
  const location = useLocation();
  const { showErrorToast } = useToast();

  const cancellationProtectionConfig = useCancellationProtectionConfig();
  const isFeatureEnabled = cancellationProtectionConfig.enabled;

  const isPayoutSheetEnabled =
    isFeatureEnabled && cancellationProtectionConfig.isPayoutSheetEnabled;

  const isOnTargetPage = TARGET_PATHS.some((path) => location.pathname.includes(path));

  const shouldFetch = isPayoutSheetEnabled && isOnTargetPage;

  const { latestPayout } = useLatestCancellationProtectionPayout({
    workerId,
    enabled: shouldFetch,
  });
  const hasAutoOpenedForPayoutId = useRef<string | undefined>();

  const { mutate: updatePayout, isLoading: isUpdating } = useUpdateCancellationProtectionPayout();
  const invalidatePayouts = useInvalidateCancellationProtectionPayouts();

  useEffect(() => {
    if (
      isDefined(latestPayout) &&
      !latestPayout.acknowledged &&
      hasAutoOpenedForPayoutId.current !== latestPayout.id
    ) {
      hasAutoOpenedForPayoutId.current = latestPayout.id;
      modalState.openModal();
    }
  }, [latestPayout, modalState]);

  const handleClose = useCallback(() => {
    if (!isDefined(latestPayout)) {
      return;
    }

    updatePayout(
      {
        payoutId: latestPayout.id,
        data: {
          data: {
            type: API_TYPES.cancellationProtectionPayout,
            id: latestPayout.id,
            attributes: { acknowledged: true as const },
          },
        },
      },
      {
        onSuccess: () => {
          void invalidatePayouts();
        },
        onSettled: (_data, error) => {
          if (error) {
            showErrorToast("Something went wrong while acknowledging the cancellation payout.");
            logError(APP_V2_APP_EVENTS.ACKNOWLEDGE_CANCELLATION_PROTECTION_PAYOUT_FAILURE, {
              error,
              metadata: { payoutId: latestPayout.id, workerId },
            });
          }

          logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_PAYOUT_DISMISSED, {
            payoutId: latestPayout.id,
            workerId,
            error: isDefined(error) ? toErrorMessage(error) : undefined,
          });

          // We'll dismiss in case of errors to as to not block the use
          modalState.closeModal();
        },
      }
    );
  }, [updatePayout, invalidatePayouts, modalState, latestPayout, showErrorToast, workerId]);

  if (!shouldFetch || !isDefined(latestPayout)) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <CancellationProtectionPayoutBottomSheet
        modalState={modalState}
        payout={latestPayout}
        isUpdating={isUpdating}
        onClose={handleClose}
      />
    </ThemeProvider>
  );
}
